/**
 * Detect English comprehension answer tables (phrase / effect / character)
 * so take/review UIs can use wide multiline cells instead of compact maths sizing.
 * Mirrors tution-student-mobile/src/utils/tableLayoutFillMobile.ts
 */

const stripHtml = (html) =>
  String(html ?? "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const PROSE_TABLE_HEADER_RE =
  // "Comparison" / "What does the comparison help the reader understand?" (NLIGSC English 2017 Q4d)
  // matched nothing here, so a six-mark comprehension table was laid out with the compact 44px
  // single-line cells built for maths grids and the student had nowhere to write.
  /\b(?:word|phrase|quotation|quote|character|impression|explain|explanation|reason|evidence|meaning|feeling|describe|comment|suggest|tells?\s+us|about\s+(?:him|her|them|it)|effect|atmosphere|language|how\s+does|why\s+(?:does|is|are)|what\s+(?:it|this|the|does|do)|example|understand(?:ing)?|comparison|simile|metaphor|imagery|technique|device|purpose|mood|tone)\b/i;

const QUANTITY_TABLE_HEADER_RE =
  /^(?:number|total|amount|quantity|how\s+many|no\.?|count)\b|\b(?:boys?|girls?|pupils?|students?|men|women|children|adults?|tickets?)\b/i;

const headerLooksLikeQuantityLabel = (text) => {
  const t = String(text || "").replace(/\s+/g, " ").trim();
  if (!t || t.length > 48) return false;
  if (PROSE_TABLE_HEADER_RE.test(t)) return false;
  return QUANTITY_TABLE_HEADER_RE.test(t);
};

const cellFixedText = (cell) => {
  if (!cell || typeof cell.b === "number") return "";
  return stripHtml(cell.f);
};

/** True when every fixed cell looks numeric (magic squares, number grids). */
export const gridLooksNumeric = (grid) => {
  if (!Array.isArray(grid) || grid.length === 0) return false;
  for (const row of grid) {
    for (const cell of row || []) {
      if (cell && typeof cell.b === "number") continue;
      const text = stripHtml(cell?.f);
      if (!text) continue;
      if (/^[\d+÷/=\-–—]+$/.test(text)) continue;
      if (/^[A-Za-z×÷+\-]{1,8}$/u.test(text)) continue;
      return false;
    }
  }
  return true;
};

/**
 * English comprehension answer grids need wide multiline cells.
 * Uses first-row headers only so tally tables ("Boys"/"Girls") stay compact.
 */
export const gridLooksProseAnswer = (grid) => {
  if (!Array.isArray(grid) || grid.length === 0) return false;
  if (gridLooksNumeric(grid)) return false;

  const firstRow = grid[0] || [];
  let longHeaders = 0;
  let veryLongHeaders = 0;
  let keywordHit = false;
  let quantityHeaders = 0;
  let textHeaders = 0;
  for (const cell of firstRow) {
    if (cell && typeof cell.b === "number") continue;
    const text = stripHtml(cell?.f);
    if (!text) continue;
    textHeaders += 1;
    if (text.length >= 14) longHeaders += 1;
    if (text.length >= 30) veryLongHeaders += 1;
    if (PROSE_TABLE_HEADER_RE.test(text)) keywordHit = true;
    if (headerLooksLikeQuantityLabel(text)) quantityHeaders += 1;
  }
  if (keywordHit) return true;
  if (quantityHeaders > 0 && quantityHeaders === textHeaders) return false;
  // One header long enough to be a whole question ("What does the comparison help the reader
  // understand?") is as good a signal as two merely longish ones — a maths grid never labels a
  // column with a sentence. The second column in such a table is often a bare noun ("Comparison"),
  // which is why requiring TWO long headers missed them.
  return longHeaders >= 2 || veryLongHeaders >= 1;
};

/** Stack 2–3 column prose tables so each answer gets full width for typing. */
export const shouldStackProseTableLayout = (grid) => {
  if (!gridLooksProseAnswer(grid)) return false;
  const colCount = grid.reduce((max, r) => Math.max(max, r ? r.length : 0), 0);
  return colCount >= 2 && colCount <= 3;
};

export const proseHeaderLabels = (grid) => {
  const colCount = grid.reduce((max, r) => Math.max(max, r ? r.length : 0), 0);
  const firstRow = grid[0] || [];
  return Array.from({ length: colCount }, (_, colIdx) => cellFixedText(firstRow[colIdx]));
};

export const rowHasEditableBlank = (row) =>
  (row || []).some((cell) => cell && typeof cell.b === "number");
